import { useState } from 'react';
import AppleSignin from 'react-apple-signin-auth';
import { Button } from '@mui/material';
import { useDispatch } from 'react-redux';
import { LoadingScreen } from '../LoadingScreen';
import { setAuthUser } from '../../../../redux/slices/AuthUser';
import axiosInstance from '../../../../axios/axiosInstance';
import { decodeToken } from '../../../../helper/decodeToken';
import useNavigateBasedOnPath from '../../helper/checkPrevPath';
import { useShowAlert } from '../../../../hooks/useShowAlert';

const AppleLoginButton = ({
  children = 'Login with Apple',
  buttonSx = null,
  endIcon = null,
  startIcon = null,
  variant = 'contained',
  onSuccess,
}) => {
  const { showAlert } = useShowAlert();
  const dispatch = useDispatch();
  const navigateBasedOnPath = useNavigateBasedOnPath();
  const CLIENTID = import.meta.env.VITE_VERCEL_SERVICES_ID_APPLE;
  const REDIRECTURI = import.meta.env.VITE_VERCEL_APPLE_REDIRECT_URI;
  const [loading, setLoading] = useState(false);

  // Apple isn't configured yet (no Services ID) — render a disabled button so the
  // option is visible but inert, matching the Google/Facebook fallbacks.
  if (!CLIENTID || !REDIRECTURI) {
    return (
      <Button
        variant={variant}
        startIcon={startIcon}
        endIcon={endIcon}
        disabled
        sx={{
          ...(buttonSx || {
            height: '50px',
            width: '244px',
            borderRadius: '5px',
            mt: '10px',
            mb: '10px',
          }),
          opacity: 0.65,
          '&.Mui-disabled': {
            color: buttonSx?.color || 'inherit',
            borderColor: buttonSx?.borderColor || undefined,
            backgroundColor: buttonSx?.backgroundColor || undefined,
          },
        }}
      >
        {children}
      </Button>
    );
  }

  // handle response from Apple and send identity token to server
  const responseApple = (response) => {
    const identityToken = response?.authorization?.id_token;
    if (!identityToken) {
      showAlert('error', 'Apple did not return a valid sign-in token');
      return;
    }
    // Apple only sends the name on the first authorization.
    const firstName = response?.user?.name?.firstName;
    const lastName = response?.user?.name?.lastName;

    setLoading(true);
    axiosInstance
      .post('/user/auth/apple', { identityToken, firstName, lastName })
      .then(async (res) => {
        const token = res.data?.token;
        const { id, exp } = await decodeToken(token);
        if (!token || !id || !exp) {
          throw new Error('The server returned an invalid login session');
        }
        localStorage.setItem('token', token);
        dispatch(setAuthUser({ user: res.data?.user || null, exp, id }));
        setLoading(false);
        onSuccess?.(res.data?.user || null);
        navigateBasedOnPath();
      })
      .catch((err) => {
        showAlert('error', err.response?.data?.error || err.response?.data?.message || err.message);
        setLoading(false);
      });
  };

  const authOptions = {
    clientId: CLIENTID,
    redirectURI: REDIRECTURI,
    scope: 'name email',
    usePopup: true,
  };

  return (
    <>
      <AppleSignin
        authOptions={authOptions}
        onSuccess={responseApple}
        onError={(err) => {
          // The user closing the popup surfaces as an error with no message.
          if (err?.error && err.error !== 'popup_closed_by_user') {
            showAlert('error', err.error);
          }
        }}
        render={({ onClick }) => (
          <Button
            variant={variant}
            startIcon={startIcon}
            endIcon={endIcon}
            onClick={onClick}
            sx={
              buttonSx || {
                height: '50px',
                width: '244px',
                borderRadius: '5px',
                mt: '10px',
                mb: '10px',
                background: '#000',
                color: '#fff',
              }
            }
          >
            {children}
          </Button>
        )}
      />
      <LoadingScreen loadingState={loading} />
    </>
  );
};

export default AppleLoginButton;
